import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  Paper,
  Typography,
  LinearProgress,
  Button,
  Dialog,
  ToggleButtonGroup, 
  ToggleButton,
  Box,
  DialogTitle,
  DialogActions,
  Divider,
} from '@mui/material';
import {NavigateFunction, useNavigate, useParams} from "react-router-dom";
import {useToolbar} from "../../components/ToolbarContext";
import {EstimateTreeDto} from "../../shared/dtos/estimate-tree.dto";
import {EstimateTreeOperationBulkUpdateRequestEntryDto, EstimateTreeOperationBulkUpdateResponseEntryDto} from "../../shared/dtos/estimate-tree-operation.dto";
import {requireAuth} from "../../shared/services/auth.service";
import {getEstimate, updateEstimateStatus, getAiEstimationStatus, refreshEstimatePricingRules} from "../estimate/estimate.service";
import {updateEstimateTreeOperationsBulk} from "../estimate/estimate-tree.service";
import {logError} from "../../shared/services/logger.service";
import {enqueueSnackbar} from "notistack";
import {getClient} from "../client/clients.service";

import {EstimateStatus, EstimateStatusType} from "../../shared/dtos/estimate.dto";
import {ExternalCompanyDto} from "../../shared/dtos/company.dto";
import {getCompany} from "../../shared/services/company.service";
import {distributeMinutes, effectiveLaborMinutes} from "../../shared/services/operation-time.service";
import {EstimateItemizedDisplayMode, EstimateItemizedStateless} from "./EstimateItemizedStateless";
import { EstimateWithComponentsDto } from '../../shared/dtos/estimate-with-components.dto';
import EstimateMapWorkspaceComponent from '../estimate/EstimateMapWorkspaceComponent';
import SplitScreenLayout from '../../shared/components/SplitScreenLayout';
import RealTimeService from '../../shared/services/real-time.service';
import { replaceItemInArray } from '../../shared/misc/react-state-utils';
import { CommunicationEditor } from '../../components/communications';
import {useOptionalPermissions} from '../../shared/permissions/PermissionContext';
import {PermissionCode} from '../../shared/permissions/permissions';
import {getLibraryEstimate} from '../admin/library/library.service';

import {formatAddressLine} from "../../shared/misc/location-helpers";
// export type TreeWithOperation = EstimateTreeDto & {operations: EstimateTreeOperationDto[], operationMethods?: EstimateTreeOperationMethodDto[]};

type EstimateItemizedPageToolbarData = {
  estimateId: number,
  displayMode: EstimateItemizedDisplayMode,
}

const EstimateItemizedPage: React.FC = () => {

  const invoiceRef = useRef<HTMLDivElement>(null);
  // SQ-283: the rules refresh runs once per page load; the AI status handler
  // (mounted once) reaches the latest silent refresh through a ref.
  const pricingRulesRefreshedRef = useRef(false);
  const refreshDataSilentlyRef = useRef<() => Promise<void>>();

  const routeParams = useParams<Record<string, string>>();
  const treeIdParam = routeParams["treeId"];
  const initialTreeId = treeIdParam ? parseInt(treeIdParam) : undefined;
  const estimateId = parseInt((routeParams["estimateId"])!);
  const navigate = useNavigate();
  const toolbarContext = useToolbar<EstimateItemizedPageToolbarData>();
  const {me, hasPermission} = useOptionalPermissions();
  // SQ-500: staff and the company's library curators see "Prepare for library" on a completed job where the library is enabled.
  const [libraryPreparable, setLibraryPreparable] = useState(false);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [bulkUpdateIsLoading, setBulkUpdateIsLoading] = useState<boolean>(false);
  const [estimate, setEstimate] = useState<EstimateWithComponentsDto>();
  const [client, setClient] = useState<{name: string, address: string, email?: string, phoneNumber?: string}>();
  const [company, setCompany] = useState<ExternalCompanyDto>();
  const [displayMode, setDisplayMode] = useState<EstimateItemizedDisplayMode>('admin');
  const [aiWarningModalOpen, setAiWarningModalOpen] = useState(false);
  const canEditStatus = hasPermission(PermissionCode.EstimatesEdit) && !([EstimateStatus.EstimateDeleted, EstimateStatus.EstimateApproved] as EstimateStatusType[]).includes(estimate?.status ?? EstimateStatus.EstimateDeleted);
  const canEditContent = hasPermission(PermissionCode.EstimatesEdit) && ([EstimateStatus.EstimateInProgress, EstimateStatus.EstimateCompleted, EstimateStatus.EstimatePendingReSend] as EstimateStatusType[]).includes(estimate?.status ?? EstimateStatus.EstimateDeleted);
  const canSendEstimate = hasPermission(PermissionCode.EstimatesSend);

  // A work item (tree) with no operations has nowhere to store a price, so it must
  // gain at least one operation before the estimate can be completed. Mirrors the
  // API guard in EstimateService.UpdateEstimateStatus.
  const workItemsMissingOperations = useMemo(() => {
    const trees = (estimate as EstimateWithComponentsDto | undefined)?.trees ?? [];
    return trees.filter(tree => (tree.operations?.length ?? 0) === 0).length;
  }, [estimate]);

  const handleEditClick = useCallback(() => {
    if(!canEditContent) {
      enqueueSnackbar('You cannot edit this estimate', {variant: 'error', autoHideDuration: 5000});
      return;
    }
    if(estimate?.status !== EstimateStatus.EstimateInProgress) {
      updateEstimateStatus(navigate, estimateId, { status: EstimateStatus.EstimateInProgress })
        .then(() => {
          setEstimate(prevEstimate => ({ ...prevEstimate!, status: EstimateStatus.EstimateInProgress }));
        })
        // SQ-283: entering edit mode prices the estimate at the company's
        // current pricing rules; re-read so the "Added for …" rows are the
        // new copy. A refused refresh (approved) or a failure changes nothing.
        .then(() => { pricingRulesRefreshedRef.current = true; return refreshEstimatePricingRules(navigate, estimateId).catch(() => false); })
        .then(() => refreshDataSilently())
        .catch(e => {
          logError('Error updating estimate status', {}, e);
          enqueueSnackbar(`Error updating estimate status: ${e}`, {variant: 'error', autoHideDuration: 5000});
        });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEditContent, estimate, estimateId, navigate]);

  const toolbarActions = useCallback((_data: EstimateItemizedPageToolbarData) => (
    <div style={{display: 'flex', gap: '10px', alignItems: 'center'}} >
      {libraryPreparable && (
        <Button variant='outlined' color='inherit' onClick={() => navigate(`/admin/library/estimates/${estimateId}`)}>Prepare for library</Button>
      )}
      {canEditStatus && estimate?.status !== EstimateStatus.EstimateInProgress && (
        <Button variant='outlined' color='inherit' onClick={() => handleEditClick()}>Edit</Button>
      )}
    </div>
  ), [estimate, canEditStatus, handleEditClick, libraryPreparable, navigate, estimateId]);

  useEffect(() => {
    if (me?.isAdmin !== true && me?.canCurateLibrary !== true) return;
    const controller = new AbortController();
    // The server decides staff access and completion; any refusal simply hides the action.
    getLibraryEstimate(navigate, estimateId, controller.signal)
      .then(state => setLibraryPreparable(state.estimate.jobCompleted))
      .catch(() => setLibraryPreparable(false));
    return () => controller.abort();
  }, [me?.isAdmin, me?.canCurateLibrary, navigate, estimateId]);

  const hasInProgressAiEstimatedCostGeneration = useCallback(async () => {
    const status = await getAiEstimationStatus(navigate, estimateId)
    return status.aiEstimatedCostGenerationStatus === 'InProgress' || status.aiEstimatedCostGenerationStatus === 'InProgressWithFailures';
  }, [navigate, estimateId]);

  const internalFetchData = useCallback(async (navigate: NavigateFunction, estimateId: number) => {
    return requireAuth(navigate)
      .then(() => getEstimate(navigate, estimateId))
      .then(async e => {
        // SQ-283: an in-progress estimate opens straight into edit mode,
        // which counts as entering it, so the record takes the company's
        // current pricing rules once per page load (the server answers 204
        // and the re-read below carries the new copy; 409 or a failure
        // leaves the current copy, which keeps pricing).
        if (e.status === EstimateStatus.EstimateInProgress && hasPermission(PermissionCode.EstimatesEdit) && !pricingRulesRefreshedRef.current) {
          pricingRulesRefreshedRef.current = true;
          const refreshed = await refreshEstimatePricingRules(navigate, estimateId).catch(() => false);
          if (refreshed) e = await getEstimate(navigate, estimateId);
        }
        const client = await getClient(navigate, e.clientId)
        const company = await getCompany(navigate);
        setEstimate(e);
        setClient({...client, address: formatAddressLine(client.address, client.city, client.state, client.postalCode)});
        setCompany(company);
        setDisplayMode('admin')
        const toolbarData = {estimateId, displayMode: 'admin' as const};
        toolbarContext.initToolbar('EstimateItemizedPage', `${formatAddressLine(e.address, e.city, e.state, e.postalCode)}`, toolbarData, toolbarActions);
      })
      .catch(e => {
        logError('Error fetching estimate', {}, e)
        enqueueSnackbar(`Error fetching estimate: ${e}`, {variant: 'error', autoHideDuration: 5000});
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, toolbarActions, setEstimate, setClient, setCompany, setDisplayMode]);

  const refreshData = useCallback(() => {
    setIsLoading(true);
    return internalFetchData(navigate, estimateId)
      .catch(e => {
        logError('Error fetching estimate', {}, e)
        enqueueSnackbar(`Error fetching estimate: ${e}`, {variant: 'error', autoHideDuration: 5000});
      })
      .finally(() => {
        setIsLoading(false)
      });
  }, [navigate, estimateId, internalFetchData]);

  // Silent refresh for auto-refresh - doesn't set loading state to avoid page flashing
  const refreshDataSilently = useCallback(() => {
    return internalFetchData(navigate, estimateId)
      .catch(e => {
        logError('Error auto-refreshing estimate', {}, e)
        // Don't show error snackbar for silent refresh to avoid spamming user
      });
  }, [navigate, estimateId, internalFetchData]);
  refreshDataSilentlyRef.current = refreshDataSilently;

  const handleCompleteEstimateClick = useCallback(async () => {
    try {
      if (workItemsMissingOperations > 0) {
        enqueueSnackbar(`This estimate can't be completed because ${workItemsMissingOperations} work item(s) have no operations. Add at least one operation to every work item first.`, {variant: 'error', autoHideDuration: 7000});
        return;
      }

      const hasInProgressAi = await hasInProgressAiEstimatedCostGeneration();
      if (hasInProgressAi) {
        setAiWarningModalOpen(true);
        return;
      }
      
      await updateEstimateStatus(navigate, estimateId, {status: EstimateStatus.EstimateCompleted});
      await refreshDataSilently();
      enqueueSnackbar('Estimate completed', {variant: 'success', autoHideDuration: 3000});
    } catch (e) {
      logError('Error completing estimate', {}, e as Error);
      enqueueSnackbar(`Error completing estimate: ${e}`, {variant: 'error', autoHideDuration: 5000});
    } finally {
      refreshData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, estimateId, refreshData, refreshDataSilently, workItemsMissingOperations]);

  const handleAiWarningModalClose = useCallback(() => {
    setAiWarningModalOpen(false);
  }, [setAiWarningModalOpen]);

  const handleCompleteEstimateWithAiWarning = useCallback(async () => {
    setAiWarningModalOpen(false);
    try {
      if (workItemsMissingOperations > 0) {
        enqueueSnackbar(`This estimate can't be completed because ${workItemsMissingOperations} work item(s) have no operations. Add at least one operation to every work item first.`, {variant: 'error', autoHideDuration: 7000});
        return;
      }

      await updateEstimateStatus(navigate, estimateId, {status: EstimateStatus.EstimateCompleted});
      await refreshDataSilently();
      enqueueSnackbar('Estimate completed', {variant: 'success', autoHideDuration: 3000});
    } catch (e) {
      logError('Error completing estimate', {}, e as Error);
      enqueueSnackbar(`Error completing estimate: ${e}`, {variant: 'error', autoHideDuration: 5000});
    } finally {
      refreshData();
    }
  }, [navigate, estimateId, refreshData, refreshDataSilently, workItemsMissingOperations]);

  useEffect(() => {
    refreshData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update toolbar when estimate changes to reflect status updates
  useEffect(() => {
    if (estimate) {
      const toolbarData = {estimateId, displayMode};
      toolbarContext.initToolbar('EstimateItemizedPage', `${formatAddressLine(estimate.address, estimate.city, estimate.state, estimate.postalCode)}`, toolbarData, toolbarActions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimate, displayMode, toolbarActions]);

  // SQ-712: a company without AI pricing takes no AI update (the recovery job can still report an old run as failed).
  const aiPricingRef = useRef(false);
  aiPricingRef.current = company?.aiPricingEnabled === true;

  useEffect(() => {
    RealTimeService.getInstance().onAiEstimatedCostStatusUpdated((estimateId, treeId, treeOperationId, status, newAiEstimatedCost) => {
      if (!aiPricingRef.current) return;
      console.log('RTS AiEstimatedCostStatusUpdated', estimateId, treeId, treeOperationId, status, newAiEstimatedCost);
      setEstimate(prevEstimate => {
        // The event can arrive before the estimate has loaded; the initial fetch
        // will return the current server state, so there is nothing to patch yet.
        if (!prevEstimate) return prevEstimate;
        const newEstimate = { 
        ...prevEstimate,
        trees: replaceItemInArray(
          prevEstimate.trees,
          tree => tree.id === treeId,
          tree => ({ 
            ...tree, 
            operations: replaceItemInArray(
              tree.operations, 
              op => op.id === treeOperationId, 
              op => ({ ...op, aiEstimatedCostGenerationStatus: status, aiEstimatedCost: newAiEstimatedCost ?? op.aiEstimatedCost, estimatedCost: newAiEstimatedCost ?? op.estimatedCost })
            )
          })
        )
      };
      // totalCost also carries equipment cost and additional cost items, which
      // aren't recomputable from trees — apply the labor delta to it instead.
      // SQ-283: the small-job and minimum amounts move with labor too, so the
      // local total is provisional; the silent refetch below replaces it.
      const laborSum = (trees: EstimateTreeDto[]) => trees.reduce((acc, tree) => acc + (tree.operations.reduce((acc, op) => acc + (op.estimatedCost ?? 0), 0)), 0);
      const newTotalCost = prevEstimate.totalCost + laborSum(newEstimate.trees) - laborSum(prevEstimate.trees);
      return {...newEstimate, totalCost: newTotalCost};
    });
    refreshDataSilentlyRef.current?.();
    });
  }, []);


  const handleDisplayTypeChange = useCallback((event: React.MouseEvent<HTMLElement>, displayType: string | null) => {
    if(!displayType) return;
    setDisplayMode(displayType as 'admin' | 'customer' | 'contractor')
  }, [setDisplayMode]);

  const updateTreesFromBulkUpdate = useCallback((updatedOperations: EstimateTreeOperationBulkUpdateResponseEntryDto[]) => {
    setEstimate(prevEstimate => ({ 
      ...prevEstimate!,
      trees: prevEstimate!.trees.map(tree => {
        const updatedTree = updatedOperations.find(op => op.treeId === tree.id);
        return updatedTree ? {...tree, operations: updatedTree.operations} : tree;
      })
    }));
  }, [setEstimate]);

  const performBulkUpdate = useCallback(async (updates: EstimateTreeOperationBulkUpdateRequestEntryDto[], operationName: string) => {
    setBulkUpdateIsLoading(true);
    try {
      const updatedOperations = await updateEstimateTreeOperationsBulk(navigate, estimateId, updates);
      updateTreesFromBulkUpdate(updatedOperations);
      await refreshDataSilently();
      enqueueSnackbar(`${operationName} updated successfully`, {variant: 'success', autoHideDuration: 3000});
    } catch (e) {
      logError(`Error updating ${operationName.toLowerCase()}`, {}, e as Error);
      enqueueSnackbar(`Error updating ${operationName.toLowerCase()}: ${e}`, {variant: 'error', autoHideDuration: 5000});
    } finally {
      setBulkUpdateIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, estimateId, refreshDataSilently]);

  // SQ-522: the steppers price at the crew's own rate, which the estimate
  // carries (null until a crew is picked — the steppers then wait).
  const billedHourlyLaborRate = (estimate as EstimateWithComponentsDto | undefined)?.laborRatePerHour ?? undefined;

  // Time-first steppers (AC-267 B1, Android parity): a tap is ±15 wall-clock
  // minutes = ±15 × crew labor minutes. Updates PATCH minutes only; the
  // server derives every price and the bulk echo refreshes the display.
  const crewSize = ((estimate as EstimateWithComponentsDto | undefined)?.crewSize ?? 0) > 0
    ? (estimate as EstimateWithComponentsDto).crewSize!
    : 1;

  const handleOperationTimeStep = useCallback(async (treeId: number, operationId: number, deltaWallMinutes: number) => {
    if (billedHourlyLaborRate == null) return;
    const operation = (estimate as EstimateWithComponentsDto | undefined)?.trees
      ?.find(t => t.id === treeId)?.operations.find(op => op.id === operationId);
    if (!operation) return;
    const laborMinutes = Math.max(0, effectiveLaborMinutes(operation, billedHourlyLaborRate) + deltaWallMinutes * crewSize);
    const operationUpdate = {treeId, operationId, estimatedMinutes: laborMinutes, estimatedMinutesOverride: laborMinutes};
    await performBulkUpdate([operationUpdate], 'Operation time');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [performBulkUpdate, billedHourlyLaborRate, crewSize]);

  const distributeAndUpdate = useCallback(async (trees: EstimateTreeDto[], deltaWallMinutes: number, operationName: string) => {
    if (billedHourlyLaborRate == null) return;
    const operations = trees.flatMap(tree => tree.operations.map(op => ({treeId: tree.id, operation: op})));
    if (operations.length === 0) return;
    const current = operations.map(({operation}) => effectiveLaborMinutes(operation, billedHourlyLaborRate));
    const newTotal = Math.max(0, current.reduce((acc, minutes) => acc + minutes, 0) + deltaWallMinutes * crewSize);
    const distributed = distributeMinutes(current, newTotal);
    const updates: EstimateTreeOperationBulkUpdateRequestEntryDto[] = operations.map(({treeId, operation}, index) => (
      {treeId, operationId: operation.id, estimatedMinutes: distributed[index], estimatedMinutesOverride: distributed[index]}));
    await performBulkUpdate(updates, operationName);
  }, [performBulkUpdate, billedHourlyLaborRate, crewSize]);

  const handleTreeTimeStep = useCallback(async (treeId: number, deltaWallMinutes: number) => {
    const tree = (estimate as EstimateWithComponentsDto | undefined)?.trees?.find(t => t.id === treeId);
    if (!tree) return;
    await distributeAndUpdate([tree], deltaWallMinutes, 'Tree time');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distributeAndUpdate]);

  const handleTotalTimeStep = useCallback(async (deltaWallMinutes: number) => {
    const trees = (estimate as EstimateWithComponentsDto | undefined)?.trees ?? [];
    await distributeAndUpdate(trees, deltaWallMinutes, 'Total time');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distributeAndUpdate]);

  // Determine send button disabled state and reason based on estimate status
  const getSendDisabledAndReason = useCallback((status: EstimateStatusType): { disabled: boolean; reason: string } => {
    switch (status) {
      case EstimateStatus.EstimateInProgress:
        return { disabled: true, reason: 'The estimate must be completed before sending.' };
      case EstimateStatus.EstimateApproved:
        return { disabled: true, reason: 'This estimate has already been approved.' };
      case EstimateStatus.EstimateDeleted:
        return { disabled: true, reason: 'This estimate has been deleted.' };
      case EstimateStatus.EstimateCompleted:
      case EstimateStatus.EstimatePendingReSend:
      case EstimateStatus.EstimatePendingApproval:
      case EstimateStatus.EstimateCancelled:
      default:
        return { disabled: false, reason: '' };
    }
  }, []);

  // Handle send completion - update status to Pending Approval when applicable
  const handleSendComplete = useCallback(async () => {
    if (!estimate) return;
    
    const status = estimate.status as EstimateStatusType;
    // Update to Pending Approval if currently: Completed, Pending Resend, or Cancelled
    const statusesThatShouldUpdate: EstimateStatusType[] = [
      EstimateStatus.EstimateCompleted,
      EstimateStatus.EstimatePendingReSend,
      EstimateStatus.EstimateCancelled,
    ];
    const shouldUpdateStatus = statusesThatShouldUpdate.includes(status);
    
    if (shouldUpdateStatus) {
      try {
        await updateEstimateStatus(navigate, estimateId, { status: EstimateStatus.EstimatePendingApproval });
        setEstimate(prevEstimate => ({ ...prevEstimate!, status: EstimateStatus.EstimatePendingApproval }));
      } catch (error) {
        logError('Error updating estimate status after send', {}, error as Error);
      }
    }
    
    refreshDataSilently();
  }, [estimate, navigate, estimateId, refreshDataSilently]);

  const estimatePreview = useMemo(() => {
    const sendState = estimate ? getSendDisabledAndReason(estimate.status as EstimateStatusType) : { disabled: true, reason: '' };
    
    return (estimate && <Paper sx={{
      display: 'inline-flex', 
      flexDirection: 'column', 
      justifyContent: 'flex-start', 
      alignItems: 'center', 
      padding: 3,
      borderRadius: { xs: 0, lg: '4px 0 0 4px' },
      boxSizing: 'border-box'
    }}>
      <ToggleButtonGroup
        color="primary"
        value={displayMode}
        exclusive
        onChange={handleDisplayTypeChange}
        style={{marginBottom: '10px'}}
      >
        <ToggleButton value="admin">Admin</ToggleButton>
        <ToggleButton value="customer">Customer</ToggleButton>
        <ToggleButton value="contractor">Contractor</ToggleButton>
      </ToggleButtonGroup>
      <Box sx={{ flex: 1, width: '100%' }}>
        <EstimateItemizedStateless 
          ref={invoiceRef} 
          company={company!} 
          client={client!} 
          estimate={estimate} 
          displayType={'estimate'} 
          displayMode={displayMode}
          editable={canEditContent && estimate.status === EstimateStatus.EstimateInProgress}
          displayNameAndDate={`Estimate #${estimate.id} - Date: ${new Date(estimate.createdAt).toLocaleDateString()}`}
         
          onOperationTimeStep={handleOperationTimeStep}
          onTreeTimeStep={handleTreeTimeStep}
          onTotalTimeStep={handleTotalTimeStep}
          crewSize={crewSize}
          crewCount={(estimate as EstimateWithComponentsDto).crewCount}
          billedHourlyLaborRate={billedHourlyLaborRate}
          isLoading={bulkUpdateIsLoading}/>
        
        {/* Complete the Estimate button - only shown for In Progress status */}
        {canEditContent && estimate.status === EstimateStatus.EstimateInProgress && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mt: 3 }}>
            <Button 
              variant='contained' 
              color='primary' 
              onClick={handleCompleteEstimateClick}
              size="large"
              disabled={workItemsMissingOperations > 0}
            >
              Complete Estimate
            </Button>
            {workItemsMissingOperations > 0 && (
              <Typography variant="body2" color="error" align="center">
                {`${workItemsMissingOperations} work item(s) have no operations. Add at least one operation to every work item before completing.`}
              </Typography>
            )}
          </Box>
        )}
        
        {/* Visual divider separating estimate content from send actions */}
        {displayMode !== 'contractor' && canSendEstimate && (
          <>
            <Divider sx={{ my: 4 }} />
            
            {/* Send Information Section */}
            <CommunicationEditor
              documentType="estimate"
              documentId={estimate.id}
              primaryContact={{
                name: client?.name,
                email: client?.email,
                phoneNumber: client?.phoneNumber
              }}
              clientName={client?.name}
              sendDisabled={sendState.disabled}
              sendDisabledReason={sendState.reason}
              onSendComplete={handleSendComplete}
            />
            
            {/* Spacer below send button for better usability (approx 1 inch / 96px) */}
            <Box sx={{ height: '96px' }} />
          </>
        )}
      </Box>
    </Paper>
  )}, [estimate, displayMode, company, client, bulkUpdateIsLoading, billedHourlyLaborRate, crewSize, handleDisplayTypeChange, handleOperationTimeStep, handleTreeTimeStep, handleTotalTimeStep, handleCompleteEstimateClick, getSendDisabledAndReason, handleSendComplete, workItemsMissingOperations, canEditContent, canSendEstimate]);

  const estimateMap = useMemo(() => {
    return (
    <EstimateMapWorkspaceComponent 
      estimate={estimate as EstimateWithComponentsDto}
      setEstimate={setter => {
        setEstimate(prev => {
          const newEstimateData = setter(prev! as EstimateWithComponentsDto); // We only will update an EstimateWithComponentsDto, if its a customer facing estimate it is readonly
          return newEstimateData
        })
      }}
      company={company!}
      initialTreeId={initialTreeId}
      urls={{
        getBaseUrl: () => `/estimates/${estimateId}/itemized`,
        getSelectedTreeUrl: (treeId: number) => `/estimates/${estimateId}/itemized/tree/${treeId}`,
      }}
      mode={estimate?.status === EstimateStatus.EstimateInProgress ? 'edit' : 'read-only'}
      sidebarEnabled={false}
    />
    // eslint-disable-next-line react-hooks/exhaustive-deps
  )}, [estimateId, estimate, setEstimate, company]);

  return (
    isLoading ? <LinearProgress/> :
    !estimate?.trees ? <Typography variant="h4">No trees found</Typography> :
    !client ? <Typography variant="h4">No client</Typography> :
    <>
      <SplitScreenLayout leftPanel={estimatePreview} rightPanel={estimateMap} breakpoint="lg" smallScreenInitialState={estimate?.status === EstimateStatus.EstimateInProgress ? 'right-panel-open' : 'left-panel-open'}/>
      <Dialog open={aiWarningModalOpen} onClose={handleAiWarningModalClose}>
        <DialogTitle>AI Operations In Progress</DialogTitle>
        <Box sx={{ padding: 2 }}>
          <Typography>
            There are AI operations currently in progress. If you complete this estimate now, any in-progress AI operations will be ignored and their results will not be applied.
          </Typography>
          <Typography sx={{ marginTop: 2, fontWeight: 'bold' }}>
            Are you sure you want to continue?
          </Typography>
        </Box>
        <DialogActions sx={{ justifyContent: 'space-around', padding: '8px' }}>
          <Button onClick={handleAiWarningModalClose} variant="contained" color="primary" style={{color: 'black'}}>
            Cancel
          </Button>
          <Button onClick={handleCompleteEstimateWithAiWarning} variant="contained" color="warning" style={{color: 'black'}} autoFocus>
            Complete Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EstimateItemizedPage;